import React from "react"
import { twMerge } from "tailwind-merge"

import { generateColorPalette } from "../utils/enhancedColorUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl, buildDefaultPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollReveal } from "./shared/ScrollReveal"

const CTABannerSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  // Layout: prefer explicit layout, otherwise derive from centerAlign flag
  const derivedLayout = settings.centerAlign === false ? "left_aligned" : "centered"
  const layout = settings.layout || derivedLayout
  const showCTA = settings.showCTA !== false
  const buttonText = settings.buttonText || settings.ctaText || "Learn more"
  const buttonAction = settings.buttonAction || settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""
  const showBackgroundImage = settings.showBackgroundImage !== false
  const backgroundImage = settings.backgroundImageUrl || settings.backgroundImage || section.imageUrl

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const backgroundColor = themeColors.bgLight || "#FFFFFF"
  const titleColor = settings.titleColor || theme?.colors?.textLight || "#FFFFFF"
  const subheadlineColor = settings.subheadlineColor || theme?.colors?.textLight || "#FFFFFF"
  const descriptionColor = settings.descriptionColor || theme?.colors?.textLight || "#FFFFFF"
  const showIcon = settings.showIcon !== false
  const iconType = settings.iconType || "arrow"

  // Icon SVG renderer
  const renderIcon = () => {
    if (!showIcon) return null

    const iconProps = {
      className: "h-4 w-4",
      fill: "none",
      viewBox: "0 0 24 24",
      stroke: "currentColor",
      strokeWidth: 2
    }

    switch (iconType) {
      case "arrow":
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        )
      case "phone":
        return (
          <svg {...iconProps}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
        )
      case "calendar":
        return (
          <svg {...iconProps}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        )
      case "email":
        return (
          <svg {...iconProps}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        )
      case "star":
        return (
          <svg {...iconProps}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        )
      default:
        return null
    }
  }

  const handleCTAClick = () => {
    switch (buttonAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        window.location.href = buildDefaultPageUrl(practice, "contact", "contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (settings.buttonUrl) {
          const url = settings.buttonUrl
          if (url) window.open(url, "_blank")
        }
        break
      case "phone":
        if (settings.phoneNumber) {
          window.location.href = `tel:${settings.phoneNumber}`
        }
        break
      case "email":
        if (settings.email) {
          window.location.href = `mailto:${settings.email}`
        }
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-6">
      {/* Text Content */}
      <div className="flex flex-col gap-2 text-center">
        {section.headline && (
          <h2
            className="text-[24px] font-bold leading-[28px]"
            style={{
              fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: titleColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className="text-[16px] leading-[22px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: subheadlineColor
            }}>
            {section.subheadline}
          </p>
        )}
        {section.body && (
          <p
            className="text-[16px] leading-[22px]"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: descriptionColor
            }}>
            {section.body}
          </p>
        )}
      </div>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={handleCTAClick}
          className={twMerge(
            "inline-flex items-center justify-center gap-2 px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {buttonText}
          {renderIcon()}
        </button>
      )}
    </div>
  )

  // Layout-specific rendering based on Figma design
  switch (layout) {
    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <ScrollReveal className="relative z-10 mx-auto px-4 text-center" variant="fadeUp">
            {renderContent()}
          </ScrollReveal>
        </section>
      )

    case "left_aligned":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-start py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <ScrollReveal className="relative z-10 mx-auto px-4 text-left" variant="fadeUp">
            {renderContent()}
          </ScrollReveal>
        </section>
      )

    case "split":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-between py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <ScrollReveal className="relative z-10 mx-auto max-w-6xl px-4" variant="fadeUp">
            <div className="flex flex-col items-center justify-between gap-4">
              <div className="flex-1 text-center">{renderContent()}</div>
              {showCTA && (
                <div className="shrink-0">
                  <button
                    onClick={handleCTAClick}
                    className={twMerge(
                      "inline-flex items-center justify-center gap-2 px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg",
                      getButtonClasses(theme?.buttonStyle)
                    )}
                    style={applyButtonTheme(
                      {
                        backgroundColor: theme?.colors?.primary || "#BD654F",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      },
                      theme
                    )}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    }}>
                    {buttonText}
                    {renderIcon()}
                  </button>
                </div>
              )}
            </div>
          </ScrollReveal>
        </section>
      )

    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[320px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {/* Background Image */}
          {showBackgroundImage && backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            />
          )}

          {/* Overlay */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.32)" }} />

          {/* Content */}
          <ScrollReveal className="relative z-10 mx-auto px-4 text-center" variant="fadeUp">
            {renderContent()}
          </ScrollReveal>
        </section>
      )
  }
}

export default CTABannerSectionMobile
